import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINES = [
  { text: "PLAY", cls: "text-white" },
  { text: "LOUDER.", cls: "text-stroke-y" },
  { text: "ZOOP UP.", cls: "text-[#FFEF00] sticker" },
];
const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ text, cls, i }) => (
  <span className="block overflow-hidden pb-2 -mb-2">
    <motion.span className={`block ${cls}`} initial={{ y: "110%", rotate: 4 }} animate={{ y: 0, rotate: 0 }} transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease }}>
      {text}
    </motion.span>
  </span>
);

const Badge = () => (
  <div className="absolute right-0 -top-10 md:-right-8 md:-top-10 w-28 h-28 md:w-36 md:h-36 z-20" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 100 100" className="w-full h-full spin-slow">
      <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <circle cx="50" cy="50" r="49" fill="#FFEF00" stroke="#000" strokeWidth="2" />
      <text fontFamily="Unbounded" fontWeight="800" fontSize="9.5" letterSpacing="2" fill="#000"><textPath href="#c">BOOK A LANE • WWW.ZOOPUP.IN • </textPath></text>
    </svg>
    <Sparkles className="absolute inset-0 m-auto text-black" size={28} />
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 120, damping: 18 });
  const spot = useTransform([mx, my], ([x, y]) => `radial-gradient(420px circle at ${x * 100}% ${y * 100}%, rgba(255,239,0,0.22), transparent 60%)`);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden">
      <motion.div className="absolute inset-0 pointer-events-none" style={{ background: spot }} />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#14C3F7]/15 blur-[120px]" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }} className="font-mono text-xs uppercase tracking-[0.3em] text-[#14C3F7] mb-6">
            Bowling · Arcade · VR · Go-Karts · Simulators
          </motion.p>
          <h1 data-testid="hero-heading" className="font-display font-black leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-[6.5rem]">
            {LINES.map((l, i) => <MaskLine key={l.text} {...l} i={i} />)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }} className="mt-8 max-w-lg text-base md:text-lg text-slate-300 leading-relaxed">
            The loudest playground in town. Bowling, arcade, VR, go-karts, trampolines, laser tag, sports simulators and more — plus a kitchen that keeps the party fed.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-book-btn" onClick={() => scrollToId("book")} className="btn-zoop rounded-full px-7 py-4 font-display font-bold text-sm md:text-base inline-flex items-center gap-2">
              Book your slot <ArrowDownRight size={18} />
            </button>
            <button data-testid="hero-parties-btn" onClick={() => scrollToId("parties")} className="btn-ghost rounded-full px-7 py-4 font-semibold text-sm md:text-base">Plan a party</button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.9, clipPath: "inset(12% 12% 12% 12% round 40px)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0% round 40px)" }}
            transition={{ duration: 1.3, delay: 0.35, ease }}
            className="relative aspect-[4/5] w-full max-w-[520px] mx-auto rounded-[40px] border-[3px] border-black shadow-[10px_10px_0_#14C3F7] overflow-hidden"
            data-testid="hero-image-frame"
          >
            <motion.img style={{ y: imgY, scale: 1.2 }} src="/img/zones/bowling.jpg" alt="Neon bowling lanes at ZoopUp" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07080E]/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between" style={{ transform: "translateZ(60px)" }}>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFEF00]">Now playing</p>
                <p className="font-display font-extrabold text-2xl">Midnight Bowling</p>
              </div>
              <span className="w-3 h-3 rounded-full bg-[#FF2A85] animate-pulse" />
            </div>
          </motion.div>
          <Badge />
        </div>
      </div>
    </section>
  );
};
